import type { ApiBudgetCategory, BudgetGroupExpanded, TeamBudgetExpanded } from '@/types/budgetGroups';
import type { ApiTeamBudgetPositionTarget } from '@/types/budgetGroups';
import { BudgetGroupAthleteItem } from '@/types/budgetGroups';
import type { ApiAthleteTag } from '@/types/tags';
import { TeamPosition } from '@/types/positions';
import { convertCentsToDollarsDecimal } from '@/utils/helpers';
import { useAcademicYears } from '@/composables/useAcademicYears';
import { cloneDeep } from 'lodash-es';
import { getDefaultBudgetTwrnForCurrentDate } from '@/utils/budgetHelpers';
import { defineStore } from 'pinia';
import { computed, reactive, toRefs, toRaw } from 'vue';
import type { AwardCategory, ScholarshipPackageListing } from '@/types/scholarships';
import { useRosterFilter } from '@/composables/useRosterFilter';

import { i18n } from '@/plugins/i18n';

const { t } = i18n.global;
export interface PositionAllocationData {
  position: string;
  allocationValue: number;
  allocationPercentage: number;
  totalBudget: number;
}

export interface BudgetYearOverviewSummary {
  budgetTwrn: string;
  academicYearLabel?: string;
  institutional: {
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
  thirdParty: {
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
  scholarships: {
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
  scholarshipCount: {
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
  rosterSize: {
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
}

export interface BudgetYearPositionSummary {
  budgetTwrn: string;
  academicYearLabel?: string;
  positions: Array<{
    name: string;
    targetBudget: number;
    institutionalAllocated: number;
    thirdPartyAllocated: number;
    scholarshipAllocated: number;
    scholarshipEquivalencyTotal: number;
    budgetSet: boolean;
  }>;
}

export interface BudgetYearCategorySummary {
  budgetTwrn: string;
  academicYearLabel?: string;
  overallSummary: {
    name: string;
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
  };
  categories: Array<{
    twrn: string;
    name: string;
    totalBudget: number;
    allocated: number;
    budgetSet: boolean;
    isExcluded: boolean;
  }>;
}

interface BudgetGroupStoreState {
  budgetGroup: BudgetGroupExpanded | null;
  selectedBudgetTwrn: string | null;
  teamTwrn: string | null;
  athletes: BudgetGroupAthleteItem[];
  tagDefinitions: Record<string, ApiAthleteTag>;
  positionTargets: Record<string, ApiTeamBudgetPositionTarget>;
  institutionalNilCategories: Record<string, ApiBudgetCategory>;
  thirdPartyNilCategories: Record<string, ApiBudgetCategory>;
  // scholarship packages by budget twrn, then by package twrn
  scholarshipPackages: Record<string, Record<string, ScholarshipPackageListing>>;
  // award categories by budget twrn, then by award category twrn
  awardCategories: Record<string, Record<string, AwardCategory>>;
  isFiltered: boolean;
  filteredValues: {
    athletes: number;
    institutionalTotal: number;
    thirdPartyTotal: number;
  };
  filteredBudgets: TeamBudgetExpanded[];
  filteredBudgetTwrns: string[];
}

const DEFAULT_STATE: BudgetGroupStoreState = {
  budgetGroup: null,
  selectedBudgetTwrn: null,
  teamTwrn: null,
  athletes: [],
  tagDefinitions: {},
  positionTargets: {},
  institutionalNilCategories: {},
  thirdPartyNilCategories: {},
  scholarshipPackages: {},
  awardCategories: {},
  isFiltered: false,
  filteredValues: {
    athletes: 0,
    institutionalTotal: 0,
    thirdPartyTotal: 0,
  },
  filteredBudgets: [],
  filteredBudgetTwrns: [],
};

function cloneDefaultState() {
  return structuredClone(DEFAULT_STATE);
}

export const useBudgetGroupStore = defineStore('budgetGroup', () => {
  const state = reactive<BudgetGroupStoreState>(cloneDefaultState());
  const { currentAcademicYear, academicYearsMapper } = useAcademicYears();
  const { currentView, budgetPeriodFilters } = useRosterFilter();

  const setBudgetGroup = (budgetGroup: BudgetGroupExpanded) => {
    state.budgetGroup = budgetGroup;
  };

  // ============================================================================
  // BUDGET ALLOCATION SELECTION
  // ============================================================================

  const selectBudget = (budgetTwrn: string | null) => {
    state.selectedBudgetTwrn = budgetTwrn;
  };

  const selectDefaultBudget = () => {
    if (state.selectedBudgetTwrn || !state.budgetGroup) return;
    const defaultTwrns = getDefaultBudgetTwrnForCurrentDate(
      state.budgetGroup.budgets,
      currentAcademicYear.value?.uuid,
    );
    state.selectedBudgetTwrn = defaultTwrns[0] ?? null;
  };

  const selectedBudgetForAllocations = computed<TeamBudgetExpanded | null>(() => {
    const group = state.budgetGroup;
    if (!group || !state.selectedBudgetTwrn) return null;
    return group.budgets.find((b) => b.twrn === state.selectedBudgetTwrn) ?? null;
  });

  const positionAllocationData = computed<PositionAllocationData[]>(() => {
    const budget = selectedBudgetForAllocations.value;
    if (!budget) return [];

    const totalBudget = budget.institutionalNil.budgeted;
    const teamTwrn = budget.teamTwrn;
    const positionNames = new Set<string>();

    budget.athletes?.forEach((a) => {
      positionNames.add(a.position ?? 'Unspecified');
    });

    return Array.from(positionNames)
      .sort()
      .map((position) => {
        const target = budget.positionTargets?.find(
          (pt) => TeamPosition.resolveName(teamTwrn, pt.position_id) === position,
        );
        const allocationValue = convertCentsToDollarsDecimal(target?.total_budget ?? 0);
        const allocationPercentage
          = totalBudget > 0 ? (allocationValue / totalBudget) * 100 : 0;
        return {
          position,
          allocationValue,
          allocationPercentage,
          totalBudget,
        };
      });
  });

  // ============================================================================
  // TEAM BUDGETS
  // ============================================================================
  const filteredBudgets = computed<TeamBudgetExpanded[]>(() => {
    return state.budgetGroup?.budgets.filter((budget) => {
      if (!currentView.value) return true;
      if (budgetPeriodFilters.value[currentView.value]?.length === 0) return true;
      return budgetPeriodFilters.value[currentView.value]?.includes(budget.twrn);
    }) ?? [];
  });

  const filteredBudgetTwrns = computed<string[]>(() => {
    return filteredBudgets.value.map((budget) => budget.twrn);
  });

  // ============================================================================
  // BUDGET OVERVIEW Section
  // ============================================================================
  function getOptionalAcademicYearLabel(budgetGroup: BudgetGroupExpanded, academicYearDateId: string): string | undefined {
    return budgetGroup.budgets.length > 1 ? academicYearsMapper(academicYearDateId, 'long') : undefined;
  }

  // ============================================================================
  // BUDGET OVERVIEW - PER YEAR SUMMARIES
  // ============================================================================

  /**
   * Compute total institutional NIL/ Third Party NIL allocated for a budget from state.athletes.
   * Only includes non-excluded categories. Returns value in cents.
   */
  function computeAllocated(
    budget: TeamBudgetExpanded,
    isInstitutional: boolean,
  ): number {
    return state.athletes.reduce((sum, athlete) => {
      const budgetData = athlete.budgets[budget.twrn];
      if (!budgetData?.lineItem) return sum;
      const categories = isInstitutional
        ? budgetData.lineItem.institutional_nil_categories
        : budgetData.lineItem.third_party_categories;
      const exclusionMap = isInstitutional
        ? budget.institutionalNilCategories
        : budget.thirdPartyCategories;
      return sum + Object.entries(categories)
        .reduce((catSum, [catTwrn, catData]) => {
          if (exclusionMap[catTwrn]?.exclude_from_totals) return catSum;
          return catSum + catData.amount;
        }, 0);
    }, 0);
  }

  /**
   * Compute total scholarship value allocated for a budget from state.athletes.
   * Returns value in cents (caller must convert).
   */
  function computeScholarshipAllocated(budgetTwrn: string): number {
    return state.athletes.reduce((sum, athlete) => {
      return sum + (athlete.budgets[budgetTwrn]?.scholarshipCalculatedValue ?? 0);
    }, 0);
  }

  /**
   * Compute total scholarships issued (sum of equivalency percentages) for a budget from state.athletes.
   * Returns decimal value (e.g. 2.5 = 2.5 full scholarships).
   */
  function computeScholarshipsIssuedAllocated(budgetTwrn: string): number {
    return state.athletes.reduce((sum, athlete) => {
      return sum + (athlete.budgets[budgetTwrn]?.scholarship?.equivalency_percentage ?? 0);
    }, 0);
  }

  /**
   * Budget overview summary for each academic year.
   * Allocated values are computed from state.athletes.
   * Budget/target values come from TeamBudgetExpanded (already in dollars).
   */
  const budgetOverviewByYear = computed<BudgetYearOverviewSummary[]>(() => {
    if (!state.budgetGroup) return [];
    return state.budgetGroup.budgets.map((budget) => ({
      budgetTwrn: budget.twrn,
      // If there is only one budget, don't show the academic year label
      academicYearLabel: state.budgetGroup!.budgets.length > 1 ? academicYearsMapper(budget.academicYearDateId, 'long') : undefined,
      institutional: {
        totalBudget: budget.institutionalNil.budgeted,
        allocated: convertCentsToDollarsDecimal(computeAllocated(budget, true)),
        budgetSet: budget.institutionalNil.budgeted > 0,
      },
      thirdParty: {
        totalBudget: budget.thirdPartyTarget,
        allocated: convertCentsToDollarsDecimal(computeAllocated(budget, false)),
        budgetSet: budget.thirdPartyTarget > 0,
      },
      scholarships: {
        totalBudget: budget.scholarshipsAmount.budgeted,
        allocated: convertCentsToDollarsDecimal(computeScholarshipAllocated(budget.twrn)),
        budgetSet: budget.scholarshipsAmount.budgeted > 0,
      },
      scholarshipCount: {
        totalBudget: budget.scholarshipsIssued.budgeted,
        allocated: computeScholarshipsIssuedAllocated(budget.twrn),
        budgetSet: budget.scholarshipsIssued.budgeted > 0,
      },
      rosterSize: {
        totalBudget: budget.rosterSize,
        allocated: state.athletes.filter((a) => a.budgets[budget.twrn]?.lineItem?.has_roster_spot).length,
        budgetSet: budget.rosterSize > 0,
      },

    }));
  });

  // ============================================================================
  // POSITION SUMMARIES - PER YEAR
  // ============================================================================

  const positions = computed(() => {
    const positionSet = new Set<string>();
    state.athletes.forEach((athlete) => {
      positionSet.add(athlete.position ?? 'Unspecified');
    });
    return Array.from(positionSet).sort((a, b) => a.localeCompare(b));
  });

  /**
   * Position allocation summaries for each academic year.
   * Includes both institutional and third party allocated amounts per position,
   * so the component can pick which to display based on selected category.
   * Category amounts and position targets are in cents on the raw data,
   * so convertCentsToDollarsDecimal is applied here.
   */

  const positionSummariesByYear = computed<BudgetYearPositionSummary[]>(() => {
    if (!state.budgetGroup) return [];

    return state.budgetGroup.budgets.map((budget) => {
      const positionMap = new Map<string, {
        institutionalAllocated: number;
        thirdPartyAllocated: number;
        scholarshipAllocated: number;
        scholarshipEquivalencyTotal: number;
      }>();

      state.athletes.forEach((athlete) => {
        const budgetData = athlete.budgets[budget.twrn];
        if (!budgetData) return;

        const pos = athlete.position;
        if (!positionMap.has(pos)) {
          positionMap.set(pos, {
            institutionalAllocated: 0,
            thirdPartyAllocated: 0,
            scholarshipAllocated: 0,
            scholarshipEquivalencyTotal: 0,
          });
        }
        const entry = positionMap.get(pos)!;

        entry.scholarshipAllocated += budgetData.scholarshipCalculatedValue ?? 0;
        entry.scholarshipEquivalencyTotal += budgetData.scholarship?.equivalency_percentage ?? 0;

        if (!budgetData.lineItem) return;

        Object.entries(budgetData.lineItem.institutional_nil_categories).forEach(([catTwrn, catData]) => {
          if (!budget.institutionalNilCategories[catTwrn]?.exclude_from_totals) {
            entry.institutionalAllocated += catData.amount;
          }
        });

        Object.entries(budgetData.lineItem.third_party_categories).forEach(([catTwrn, catData]) => {
          if (!budget.thirdPartyCategories[catTwrn]?.exclude_from_totals) {
            entry.thirdPartyAllocated += catData.amount;
          }
        });
      });

      const positions = Array.from(positionMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, data]) => {
          const target = budget.positionTargets.find(
            (pt) => TeamPosition.resolveName(budget.teamTwrn, pt.position_id) === name,
          );
          const targetBudget = convertCentsToDollarsDecimal(target?.total_budget ?? 0);

          return {
            name,
            targetBudget,
            institutionalAllocated: convertCentsToDollarsDecimal(data.institutionalAllocated),
            thirdPartyAllocated: convertCentsToDollarsDecimal(data.thirdPartyAllocated),
            scholarshipAllocated: convertCentsToDollarsDecimal(data.scholarshipAllocated),
            scholarshipEquivalencyTotal: data.scholarshipEquivalencyTotal,
            budgetSet: targetBudget > 0,
          };
        });

      return {
        budgetTwrn: budget.twrn,
        academicYearLabel: state.budgetGroup!.budgets.length > 1 ? academicYearsMapper(budget.academicYearDateId, 'long') : undefined,
        positions,
      };
    });
  });


  // ============================================================================
  // CATEGORY SUMMARIES - PER YEAR
  // ============================================================================

  /**
   * Sum athlete allocations for a specific category within a single budget.
   * Reads from state.athletes.
   * Both institutional and third party NIL categories are included based on the isInstitutional parameter.
   * Returns value in cents (caller must convert).
   */
  function getCategoryAllocatedForBudget(
    budgetTwrn: string,
    categoryTwrn: string,
    isInstitutional: boolean,
  ): number {
    return state.athletes.reduce((sum, athlete) => {
      const budgetData = athlete.budgets[budgetTwrn];
      if (!budgetData?.lineItem) return sum;

      const categories = isInstitutional
        ? budgetData.lineItem.institutional_nil_categories
        : budgetData.lineItem.third_party_categories;

      return sum + (categories[categoryTwrn]?.amount || 0);
    }, 0);
  }
  /**
   * Build category summaries for each academic year.
   * Includes an overall summary and per-category breakdowns.
   * Category amounts are in cents on the raw data, converted to dollars here.
   * @param budgetGroup - The budget group to build summaries for.
   * @param options - The options for the summaries.
   * @returns The category summaries.
  */
  function buildCategorySummariesByYear(
    budgetGroup: BudgetGroupExpanded,
    options: {
      getCategories: (budget: TeamBudgetExpanded) => Record<string, ApiBudgetCategory>;
      isInstitutional: boolean;
      overallName: string;
      getOverallBudget: (budget: TeamBudgetExpanded) => number;
    },
  ): BudgetYearCategorySummary[] {
    return budgetGroup.budgets.map((budget) => {
      const budgetCategories = options.getCategories(budget);
      const categoryTwrns = sortCategoryTwrns(budgetCategories);

      let overallAllocated = 0;
      const categories = categoryTwrns.map((twrn) => {
        const category = budgetCategories[twrn]!;
        const allocated = getCategoryAllocatedForBudget(budget.twrn, twrn, options.isInstitutional);
        if (!category.exclude_from_totals) {
          overallAllocated += allocated;
        }
        return {
          twrn,
          name: category.name,
          totalBudget: convertCentsToDollarsDecimal(category.amount),
          allocated: convertCentsToDollarsDecimal(allocated),
          budgetSet: category.amount > 0,
          isExcluded: category.exclude_from_totals,
        };
      });

      return {
        budgetTwrn: budget.twrn,
        academicYearLabel: getOptionalAcademicYearLabel(budgetGroup, budget.academicYearDateId),
        overallSummary: {
          name: options.overallName,
          totalBudget: options.getOverallBudget(budget),
          allocated: convertCentsToDollarsDecimal(overallAllocated),
          budgetSet: options.getOverallBudget(budget) > 0,
        },
        categories,
      };
    });
  }

  /**
   * Institutional NIL category summaries for each academic year.
   * Each year includes an overall summary and per-category breakdowns.
   * Category amounts are in cents on the raw data, converted to dollars here.
   */
  const institutionalCategorySummariesByYear = computed<BudgetYearCategorySummary[]>(() => {
    const budgetGroup = state.budgetGroup;
    if (!budgetGroup) return [];
    return buildCategorySummariesByYear(budgetGroup, {
      getCategories: (b) => b.institutionalNilCategories,
      isInstitutional: true,
      overallName: t('budgetAllocation.totals.institutionalNil'),
      getOverallBudget: (b) => b.institutionalNil.budgeted,
    });
  });
    /**
     * Third Party NIL category summaries for each academic year.
     * Each year includes an overall summary and per-category breakdowns.
     * Category amounts are in cents on the raw data, converted to dollars here.
     */
  const thirdPartyCategorySummariesByYear = computed<BudgetYearCategorySummary[]>(() => {
    const budgetGroup = state.budgetGroup;
    if (!budgetGroup) return [];
    return buildCategorySummariesByYear(budgetGroup, {
      getCategories: (b) => b.thirdPartyCategories,
      isInstitutional: false,
      overallName: t('budgetAllocation.totals.thirdPartyNil'),
      getOverallBudget: (b) => b.thirdPartyTarget,
    });
  });

  // ============================================================================
  // MASTER STATE SETTERS
  // ============================================================================

  const setTeamTwrn = (teamTwrn: string) => {
    state.teamTwrn = teamTwrn;
  };

  function setInstitutionalNilCategories(budgetGroup: BudgetGroupExpanded) {
    budgetGroup.budgets.forEach((budget) => {
      Object.entries(budget.institutionalNilCategories).forEach(([categoryTwrn, category]) => {
        state.institutionalNilCategories[categoryTwrn] = category;
      });
    });
  }

  function setThirdPartyNilCategories(budgetGroup: BudgetGroupExpanded) {
    budgetGroup.budgets.forEach((budget) => {
      Object.entries(budget.thirdPartyCategories).forEach(([categoryTwrn, category]) => {
        state.thirdPartyNilCategories[categoryTwrn] = category;
      });
    });
  }

  function setTagDefinitions(budgetGroup: BudgetGroupExpanded) {
    budgetGroup.budgets.forEach((budget) => {
      budget.tagDefinitions.forEach((tag) => {
        state.tagDefinitions[tag.uuid] = tag;
      });
    });
  }

  function setScholarshipPackage(budgetTwrn: string, scholarshipPackage: ScholarshipPackageListing) {
    if (!state.scholarshipPackages[budgetTwrn]) {
      state.scholarshipPackages[budgetTwrn] = {};
    }
    state.scholarshipPackages[budgetTwrn][scholarshipPackage.twrn] = scholarshipPackage;
  }

  /** Replaces all packages for a budget so deletes are reflected after refetch. */
  function setScholarshipPackagesForBudget(budgetTwrn: string, packages: ScholarshipPackageListing[]) {
    state.scholarshipPackages[budgetTwrn] = Object.fromEntries(packages.map((p) => [p.twrn, p]));
  }

  function setAwardCategory(budgetTwrn: string, awardCategory: AwardCategory) {
    if (!state.awardCategories[budgetTwrn]) {
      state.awardCategories[budgetTwrn] = {};
    }
    state.awardCategories[budgetTwrn][awardCategory.twrn] = awardCategory;
  }

  /** Replaces all award categories for a budget so deletes are reflected after refetch. */
  function setAwardCategoriesForBudget(budgetTwrn: string, categories: AwardCategory[]) {
    state.awardCategories[budgetTwrn] = Object.fromEntries(categories.map((c) => [c.twrn, c]));
  }

  function setAthletes(budgetGroup: BudgetGroupExpanded) {
    const athletesMap = <Record<string, BudgetGroupAthleteItem>>{};
    budgetGroup.budgets.forEach((budget) => {
      budget.athletes.forEach((athlete) => {
        if (athletesMap[athlete.twrn]) {
          athletesMap[athlete.twrn]!.addBudgetData(athlete, budget.twrn);
        } else {
          const newAthlete = new BudgetGroupAthleteItem(athlete);
          newAthlete.addBudgetData(athlete, budget.twrn);
          athletesMap[athlete.twrn] = newAthlete;
        }
      });
    });
    state.athletes = Object.values(athletesMap).sort((a, b) => {
      const posA = a.position || '';
      const posB = b.position || '';

      if (posA !== posB) {
        return posA.localeCompare(posB);
      }

      return a.lastName.localeCompare(b.lastName);
    });
  }

  function setIsFiltered(isFiltered: boolean) {
    state.isFiltered = isFiltered;
  }

  function setFilteredValues(athletes: number, institutionalTotal: number, thirdPartyTotal: number) {
    state.filteredValues.athletes = athletes;
    state.filteredValues.institutionalTotal = institutionalTotal;
    state.filteredValues.thirdPartyTotal = thirdPartyTotal;
  }

  const hydrate = (budgetGroup: BudgetGroupExpanded) => {
    const clonedBudgetGroup = cloneDeep(toRaw(budgetGroup));
    setBudgetGroup(clonedBudgetGroup);
    setTeamTwrn(clonedBudgetGroup.teamTwrn);
    setTagDefinitions(clonedBudgetGroup);
    setAthletes(clonedBudgetGroup);
    setInstitutionalNilCategories(clonedBudgetGroup);
    setThirdPartyNilCategories(clonedBudgetGroup);
  };

  /**
   * Sort category TWRNs by name, with "Default Category" always first
   */
  function sortCategoryTwrns(categories: Record<string, { name: string }>): string[] {
    const DEFAULT_CATEGORY_NAME = 'Default Category';

    const twrns = Object.keys(categories);

    return twrns.sort((a, b) => {
      const nameA = categories[a]!.name;
      const nameB = categories[b]!.name;

      const isDefaultA = nameA === DEFAULT_CATEGORY_NAME;
      const isDefaultB = nameB === DEFAULT_CATEGORY_NAME;

      if (isDefaultA && !isDefaultB) return -1;
      if (!isDefaultA && isDefaultB) return 1;

      return nameA.localeCompare(nameB);
    });
  }

  // ============================================================================
  // COMPUTED GETTERS - CATEGORIES
  // ============================================================================

  const institutionalCategoryTwrns = computed(() => {
    if (!state.budgetGroup) return {};
    return state.budgetGroup.budgets.reduce<Record<string, string[]>>((acc, budget) => {
      acc[budget.twrn] = sortCategoryTwrns(budget.institutionalNilCategories);
      return acc;
    }, {});
  });

  /**
   * All institutional category TWRNs that are not excluded from totals
   */
  const budgetedInstitutionalCategoryTwrns = computed(() => {
    if (!state.budgetGroup) return {};

    return state.budgetGroup.budgets.reduce<Record<string, string[]>>((acc, budget) => {
      const budgetedCategories = Object.keys(budget.institutionalNilCategories).filter((categoryTwrn) => {
        return !state.institutionalNilCategories[categoryTwrn]?.exclude_from_totals;
      });
      acc[budget.twrn] = budgetedCategories;
      return acc;
    }, {});
  });

  const thirdPartyCategoryTwrns = computed(() => {
    if (!state.budgetGroup) return {};
    return state.budgetGroup.budgets.reduce<Record<string, string[]>>((acc, budget) => {
      acc[budget.twrn] = sortCategoryTwrns(budget.thirdPartyCategories);
      return acc;
    }, {});
  });

  const budgetedThirdPartyCategoryTwrns = computed(() => {
    if (!state.budgetGroup) return {};

    return state.budgetGroup.budgets.reduce<Record<string, string[]>>((acc, budget) => {
      const budgetedCategories = Object.keys(budget.thirdPartyCategories).filter((categoryTwrn) => {
        return !state.thirdPartyNilCategories[categoryTwrn]?.exclude_from_totals;
      });
      acc[budget.twrn] = budgetedCategories;
      return acc;
    }, {});
  });

  type AthleteEarnings = {
    institutionalNil: number;
    scholarships: number;
    thirdPartyNil: number;
    totalEarnings: number;
  };

  type MappedEarnings = Record<string, AthleteEarnings>;

  const getAthleteEarnings = (athleteTwrn: string): MappedEarnings => {
    const budgetTwrns = state.budgetGroup?.budgets.map((budget) => budget.twrn) ?? [];
    const athlete = state.athletes.find((a) => a.twrn === athleteTwrn);
    if (!athlete || !budgetTwrns.length) throw new Error(`[BudgetGroupStore] Athlete with TWRN ${athleteTwrn} not found`);

    return budgetTwrns.reduce<MappedEarnings>((earnings, budgetTwrn) => {
      const athleteBudget = athlete.budgets[budgetTwrn];
      if (!athleteBudget || !athleteBudget.lineItem) return earnings;

      const budgetEarnings = {
        institutionalNil: 0,
        thirdPartyNil: 0,
        scholarships: 0,
        totalEarnings: 0,
      };

      if (institutionalCategoryTwrns.value[budgetTwrn]) {
        const institutionalNil = institutionalCategoryTwrns.value[budgetTwrn]?.reduce((sum, categoryTwrn) => {
          const categoryInfo = state.institutionalNilCategories[categoryTwrn];

          if (!categoryInfo || categoryInfo.exclude_from_totals) return sum;

          return sum + (athleteBudget.lineItem?.institutional_nil_categories[categoryTwrn]?.amount ?? 0);
        }, 0);
        budgetEarnings.institutionalNil = institutionalNil;
      }

      if (thirdPartyCategoryTwrns.value[budgetTwrn]) {
        const thirdPartyNil = thirdPartyCategoryTwrns.value[budgetTwrn]?.reduce((sum, categoryTwrn) => {
          const categoryInfo = state.thirdPartyNilCategories[categoryTwrn];

          if (!categoryInfo || categoryInfo.exclude_from_totals) return sum;

          return sum + (athleteBudget.lineItem?.third_party_categories[categoryTwrn]?.amount ?? 0);
        }, 0);

        budgetEarnings.thirdPartyNil = thirdPartyNil;
      }

      if (athleteBudget.scholarship) {
        budgetEarnings.scholarships = athleteBudget.scholarship.award_categories.reduce((sum, category) => {
          return sum + (category.amount ?? 0);
        }, 0);
      }

      budgetEarnings.totalEarnings = budgetEarnings.institutionalNil + budgetEarnings.thirdPartyNil + budgetEarnings.scholarships;

      return {
        ...earnings,
        [budgetTwrn]: budgetEarnings,
      };
    }, {});
  };

  // ============================================================================
  // SELECTED BUDGET HELPERS
  // ============================================================================

  /**
   * Sorted category TWRNs for the currently selected budget (institutional).
   * Used by useTeamBudgetColumnPreferences to watch for category count changes.
   */
  const selectedBudgetInstitutionalCategoryTwrns = computed<string[]>(() => {
    if (!state.selectedBudgetTwrn) return [];
    return institutionalCategoryTwrns.value[state.selectedBudgetTwrn] ?? [];
  });

  /**
   * Sorted category TWRNs for the currently selected budget (third party).
   * Used by useTeamBudgetColumnPreferences to watch for category count changes.
   */
  const selectedBudgetThirdPartyCategoryTwrns = computed<string[]>(() => {
    if (!state.selectedBudgetTwrn) return [];
    return thirdPartyCategoryTwrns.value[state.selectedBudgetTwrn] ?? [];
  });

  // ============================================================================
  // SELECTED BUDGET OVERALL SUMMARIES (values in DOLLARS, matching budgetOverviewByYear)
  // ============================================================================

  const selectedBudgetOverview = computed(() => {
    if (!state.selectedBudgetTwrn) return null;
    return budgetOverviewByYear.value.find((b) => b.budgetTwrn === state.selectedBudgetTwrn) ?? null;
  });

  const selectedBudgetInstitutionalOverallSummary = computed(() => {
    const overview = selectedBudgetOverview.value;
    const institutional = overview?.institutional ?? {
      totalBudget: 0,
      allocated: 0,
      budgetSet: false,
    };
    return {
      name: t('budgetAllocation.totals.institutionalNil'),
      remaining: (institutional.totalBudget) - (institutional.allocated),
      percentage: institutional.totalBudget
        ? ((institutional.allocated / institutional.totalBudget) * 100)
        : 0,
      ...institutional,
    };
  });

  const selectedBudgetThirdPartyOverallSummary = computed(() => {
    const thirdParty = selectedBudgetOverview.value?.thirdParty ?? {
      totalBudget: 0,
      allocated: 0,
      budgetSet: false,
    };
    return {
      name: t('budgetAllocation.totals.thirdPartyNil'),
      remaining: (thirdParty.totalBudget) - (thirdParty.allocated),
      percentage: thirdParty?.totalBudget
        ? ((thirdParty.allocated / thirdParty.totalBudget) * 100)
        : 0,
      ...thirdParty,
    };
  });

  const selectedBudgetScholarshipOverallSummary = computed(() => {
    const scholarships = selectedBudgetOverview.value?.scholarships ?? {
      totalBudget: 0,
      allocated: 0,
      budgetSet: false,
    };
    return {
      name: t('budgetAllocation.totals.scholarships'),
      remaining: (scholarships.totalBudget) - (scholarships.allocated),
      percentage: scholarships?.totalBudget
        ? ((scholarships.allocated / scholarships.totalBudget) * 100)
        : 0,
      ...scholarships,
    };
  });

  const selectedBudgetScholarshipCountOverallSummary = computed(() => {
    const count = selectedBudgetOverview.value?.scholarshipCount ?? {
      totalBudget: 0,
      allocated: 0,
      budgetSet: false,
    };
    return {
      name: t('budgetAllocation.totals.scholarshipCount'),
      remaining: (count.totalBudget) - (count.allocated),
      percentage: count.totalBudget
        ? ((count.allocated / count.totalBudget) * 100)
        : 0,
      ...count,
    };
  });

  // ============================================================================
  // UPDATE MUTATIONS
  // ============================================================================

  function updateAthlete(athleteTwrn: string, updatedAthlete: BudgetGroupAthleteItem) {
    const index = state.athletes.findIndex((a) => a.twrn === athleteTwrn);
    if (index !== -1) {
      state.athletes[index] = updatedAthlete;
    }
  }

  /**
   * Update a single athlete's category allocation
   * Used for optimistic updates before API response
   */
  function updateAthleteCategoryAllocation(
    budgetTwrn: string,
    athleteTwrn: string,
    categoryTwrn: string,
    amountInCents: number,
    isInstitutional: boolean,
  ) {
    const athlete = state.athletes.find((a) => a.twrn === athleteTwrn);
    if (!athlete || !budgetTwrn) return;

    const updatedAthlete = cloneDeep(athlete);

    const categories = isInstitutional
      ? updatedAthlete.budgets[budgetTwrn]?.lineItem?.institutional_nil_categories
      : updatedAthlete.budgets[budgetTwrn]?.lineItem?.third_party_categories;

    const categoryInfo = isInstitutional
      ? state.institutionalNilCategories[categoryTwrn]
      : state.thirdPartyNilCategories[categoryTwrn];

    if (!categoryInfo || !categories) return;

    if (categories[categoryTwrn]) {
      categories[categoryTwrn].amount = amountInCents;
    } else {
      categories[categoryTwrn] = {
        amount: amountInCents,
        exclude_from_totals: categoryInfo.exclude_from_totals,
        name: categoryInfo.name,
        type: categoryInfo.type,
      };
    }

    state.athletes[state.athletes.indexOf(athlete)] = updatedAthlete;
  }

  return {
    ...toRefs(state),
    setBudgetGroup,
    selectBudget,
    selectDefaultBudget,
    selectedBudgetForAllocations,
    positionAllocationData,
    budgetOverviewByYear,
    positionSummariesByYear,
    institutionalCategorySummariesByYear,
    thirdPartyCategorySummariesByYear,
    setTagDefinitions,
    setAthletes,
    setInstitutionalNilCategories,
    setThirdPartyNilCategories,
    institutionalCategoryTwrns,
    budgetedInstitutionalCategoryTwrns,
    thirdPartyCategoryTwrns,
    budgetedThirdPartyCategoryTwrns,
    selectedBudgetInstitutionalCategoryTwrns,
    selectedBudgetThirdPartyCategoryTwrns,
    selectedBudgetOverview,
    selectedBudgetInstitutionalOverallSummary,
    selectedBudgetThirdPartyOverallSummary,
    selectedBudgetScholarshipOverallSummary,
    selectedBudgetScholarshipCountOverallSummary,
    positions,
    setIsFiltered,
    setFilteredValues,
    updateAthlete,
    updateAthleteCategoryAllocation,
    setScholarshipPackage,
    setScholarshipPackagesForBudget,
    setAwardCategory,
    setAwardCategoriesForBudget,
    hydrate,
    getAthleteEarnings,
    filteredBudgets,
    filteredBudgetTwrns,
  };
});
